import React, { useMemo } from 'react';

import { StarsIcon } from '@nw/shared/ui';
import {
  filterDateOptionsByVisibility,
  sendGAEventToDataLayer
} from '@nw/shared/utils';

import { RadioGroupVariant } from '../../types/RadioGroupTypes';

import { useField } from './informedImports';
import {
  ChooseUserRoleOption,
  ChooseUserRoleOptionIcon,
  ChooseUserRoleOptionIconContainer,
  ChooseUserRoleOptionText,
  DateSelectionRadio,
  FieldContainer,
  IconContainer,
  RadioButtonsWrapper,
  StyledRadio,
  StyledRadioGroup
} from './styledComponent';

interface RadioOptionTypes {
  label: string | number;
  value: string | number;
  condition?: string | undefined;
}

const RADIO_GROUP_OPTION_TYPES = {
  DATE_SELECTION: 'DATE_SELECTION',
  CONDITIONAL_DATE_SELECTION: 'CONDITIONAL_DATE_SELECTION'
} as const;

interface InformedRadioGroupTypes {
  defaultValue?: string;
  values: Record<string, any>;
  onChange?: (e: any) => void;
  labelClassName?: string;
  type: RadioGroupVariant;
  hideRadioButtons?: boolean;
  radioButtonPosition?: string;
  field: string;
  error?: string;
  touched: boolean;
  label?: string;
  radioOptions?: Array<RadioOptionTypes>;
  validationSchemaObject?: any;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  radioButtonClassName?: string;
  radioGroupClassName?: string;
  radioClassName?: string;
  isHidden?: boolean;
  fieldWrapperStyles?: React.CSSProperties;
  radioWrapperStyles?: React.CSSProperties;
  isDisabled?: boolean;
  hintClassName?: string;
  children?: any;
  isCustomizedRadioOptions?: boolean;
  optionsType?: string;
  maxVisibilityTime?: string;
}

export function InformedRadioGroup(props: InformedRadioGroupTypes) {
  const {
    labelClassName,
    type,
    defaultValue,
    hideRadioButtons,
    radioButtonPosition,
    radioOptions: informedRadioOptions = [],
    radioGroupClassName,
    radioButtonClassName,
    label,
    validationSchemaObject,
    fieldWrapperStyles,
    radioClassName = 'mr-4',
    radioWrapperStyles,
    isHidden,
    values,
    field,
    isDisabled,
    hintClassName,
    children,
    validateOnChange = true,
    validateOnBlur = true,
    isCustomizedRadioOptions = false,
    optionsType,
    maxVisibilityTime,
    ...otherProps
  } = props;

  const { fieldState, fieldApi, render, ref, userProps } = useField({
    ...otherProps,
    field,
    validationSchema: validationSchemaObject.fields[field],
    validateOnChange: validateOnChange,
    validateOnBlur: validateOnBlur
  });
  const { value, error } = fieldState;
  const { setValue, setTouched, setError } = fieldApi;
  const { onChange, onBlur, ...rest } = userProps;

  const onChangeFunction = (e: any) => {
    onChange && onChange(e);
    setValue(e);
    setTouched(true);
    setError('');

    if (isCustomizedRadioOptions && e === 'STUDENT') {
      sendGAEventToDataLayer({
        event: 'student_or_parent_selection',
        user_type: 'STUDENT'
      });
    } else if (isCustomizedRadioOptions && e === 'PARENT') {
      sendGAEventToDataLayer({
        event: 'student_or_parent_selection',
        user_type: 'PARENT'
      });
    }
  };

  const conditionalDateSelectionOptions = () => {
    return informedRadioOptions
      .filter((option) => {
        if (!option.condition) return true;
        try {
          return Boolean(eval(option.condition));
        } catch (error) {
          console.error('Error evaluating condition:', option.condition, error);
          return false;
        }
      })
      .map(({ condition: _, ...rest }) => rest);
  };

  const finalizedOptions = useMemo(() => {
    if (optionsType !== RADIO_GROUP_OPTION_TYPES.CONDITIONAL_DATE_SELECTION) {
      return [];
    }
    const filteredOptions = conditionalDateSelectionOptions();
    return filterDateOptionsByVisibility(filteredOptions, maxVisibilityTime);
  }, [
    optionsType,
    informedRadioOptions,
    values,
    maxVisibilityTime
  ]);

  return render(
    <FieldContainer isHidden={isHidden} css={fieldWrapperStyles}>
      <StyledRadioGroup
        value={value}
        type={type}
        label={label}
        className={radioGroupClassName}
        onChange={onChangeFunction}
        {...rest}
        labelClassName={labelClassName}
        defaultValue={defaultValue}
        errorMessage={error}
        hintClassName={hintClassName}
        hideRadioButtons={hideRadioButtons}
      >
        {children || (
          <RadioButtonsWrapper css={radioWrapperStyles}>
            {optionsType === RADIO_GROUP_OPTION_TYPES.CONDITIONAL_DATE_SELECTION &&
              finalizedOptions.map((option) => (
                <DateSelectionRadio
                  key={option.value}
                  value={option.value}
                  isSelected={value === option.value}
                  className={'dateSelectionRadio'}
                >
                  {option.label}
                </DateSelectionRadio>
              ))}
            {optionsType !== RADIO_GROUP_OPTION_TYPES.CONDITIONAL_DATE_SELECTION &&
              informedRadioOptions.map((option, index) => {
                let toRender: string | number | React.ReactElement =
                  option.label;

                if (optionsType === RADIO_GROUP_OPTION_TYPES.DATE_SELECTION) {
                  // Check condition if it exists
                  if (option.condition) {
                    try {
                      const conditionResult = eval(option.condition);
                      // If condition evaluates to false, hide the option
                      if (!conditionResult) {
                        return null;
                      }
                    } catch (error) {
                      console.error(
                        'Error evaluating condition:',
                        option.condition,
                        error
                      );
                      return null;
                      // Fallback: show the option if evaluation fails
                    }
                  }
                  // Show the option (either no condition or condition is true)
                  return (
                    <DateSelectionRadio
                      key={option.value}
                      value={option.value}
                      isSelected={value === option.value}
                      className={'dateSelectionRadio'}
                    >
                      {option.label}
                    </DateSelectionRadio>
                  );
                } else if (
                  isCustomizedRadioOptions &&
                  option.value === 'STUDENT'
                ) {
                  toRender = (
                    <ChooseUserRoleOption isSelected={option.value === value}>
                      <ChooseUserRoleOptionIconContainer>
                        <ChooseUserRoleOptionIcon
                          src="https://d14qv6cm1t62pm.cloudfront.net/niat/student-role-icon.png"
                          alt={'student-role-icon'}
                        />
                      </ChooseUserRoleOptionIconContainer>
                      <ChooseUserRoleOptionText>
                        Student
                      </ChooseUserRoleOptionText>
                      <IconContainer>
                        <StarsIcon />
                      </IconContainer>
                    </ChooseUserRoleOption>
                  );
                } else if (
                  isCustomizedRadioOptions &&
                  option.value === 'PARENT'
                ) {
                  toRender = (
                    <ChooseUserRoleOption isSelected={option.value === value}>
                      <ChooseUserRoleOptionIconContainer>
                        <ChooseUserRoleOptionIcon
                          src="https://d14qv6cm1t62pm.cloudfront.net/niat/guardian-role-icon.png"
                          alt={'guardian-role-icon'}
                        />
                      </ChooseUserRoleOptionIconContainer>
                      <ChooseUserRoleOptionText>
                        Parents
                      </ChooseUserRoleOptionText>
                    </ChooseUserRoleOption>
                  );
                }
                return (
                  <StyledRadio
                    key={option.value}
                    value={option.value}
                    radioButtonClassName={radioButtonClassName}
                    className={radioClassName}
                    isDisabled={isDisabled}
                  >
                    {toRender}
                  </StyledRadio>
                );
              })}
          </RadioButtonsWrapper>
        )}
      </StyledRadioGroup>
    </FieldContainer>
  );
}
